import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {EditEventFormConfirmationValidationSchema, EditEventFormValidationSchema} from '@/features/agenda/formSchemas'
import {eventFormValuesToUpdatePayload, eventTypeMapToLabel, getWeekPositionInMonth} from '@/features/agenda/utils'
import {useTranslation} from '@/translations/i18n'
import {DialogHeading, DialogStore, useDialogContext} from '@ariakit/react'
import {FormProvider, SubmitErrorHandler, useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {useEditEvent} from '@/features/agenda/queries/useEditEvent'
import {useMemo, useState} from 'react'
import {Switch} from '@/components/commons/switch/Switch'
import {toast} from '@/utilities/toast/toast'
import {ObjectKeys} from '@/utilities/helpers'
import dayjs from 'dayjs'
import {DayOfWeek} from '@/features/users-availability/types'
import {EventRepetitionFrequencyText} from '@/features/agenda/components/event-repetition-frequency-text/EventRepetitionFrequencyText'
import {useGetEvents} from '@/features/agenda/queries/useGetEvents'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useGetAccessTypes} from '@/features/access-types/queries/useGetAccessTypes'

export const AgendaEditEventConfirmationDialog = ({
    confirmationDialog,
    defaultValues,
    isRecurringEvent,
    isChangedSubstitute,
    isToReschedule,
    previousDate,
    parentOnSuccess,
    parentOnError
}: {
    confirmationDialog: DialogStore
    defaultValues: EditEventFormValidationSchema
    isRecurringEvent?: boolean
    isChangedSubstitute?: boolean
    isToReschedule?: boolean
    previousDate?: Date
    parentOnSuccess?: () => void
    parentOnError?: (error: Error) => void
}) => {
    const {t} = useTranslation()
    const [applyToFutureEvents, setApplyToFutureEvents] = useState(false)
    const form = useForm<EditEventFormConfirmationValidationSchema>({
        resolver: zodResolver(EditEventFormConfirmationValidationSchema),
        defaultValues
    })

    const {data: accessTypesData} = useGetAccessTypes({
        params: {
            search: 'apertura ciclo', // solution agreed with the team to avoid wasting time on this
            page: 1,
            limit: 1
        }
    })

    const openCycleAccessType = useMemo(() => {
        return accessTypesData?.pages
            .flatMap(page => page.data)
            .find(accessType => accessType.accessType == 'cycle_opening_processing')
    }, [accessTypesData])

    const dialog = useDialogContext()

    const watchPatients = useWatch({control: form.control, name: 'patients'})
    const watchIsGroupTreatment = useWatch({control: form.control, name: 'isGroupTreatment'})
    const watchConsultants = useWatch({control: form.control, name: 'consultants'})
    const watchStatus = useWatch({control: form.control, name: 'status'})
    const watchDate = useWatch({control: form.control, name: 'date'})
    const watchRepetitionData = useWatch({control: form.control, name: 'repetitionData'})

    const project = watchPatients[0].project
    const isDisabledBulkOperation = useMemo(() => {
        const dirtyFields = ObjectKeys(form.formState.dirtyFields)
        return (
            dirtyFields.includes('patients') ||
            dirtyFields.includes('consultants') ||
            dirtyFields.includes('isGroupTreatment') ||
            dirtyFields.includes('isARecovery') ||
            (watchStatus != 'provisional' && watchStatus != 'scheduled' && watchStatus != 'to_reschedule')
        )
    }, [form.formState.dirtyFields])

    const {
        data: events,
        isFetching,
        isError
    } = useGetEvents({
        params: {
            startDate: watchDate?.date && dayjs(watchDate.date).format('YYYY-MM-DD'),
            endDate: dayjs(project?.expirationDate).format('YYYY-MM-DD'),
            therapists: watchConsultants.map(ec => ec.id),
            patients: watchPatients.map(ec => ec.id)
        },
        options: {
            enabled:
                !!project &&
                !!watchDate?.date &&
                !!project?.expirationDate &&
                watchPatients.length == 1 &&
                !isDisabledBulkOperation &&
                isToReschedule
        }
    })

    const eventsToReschedule = useMemo(() => {
        if (!events) return []
        return events.filter(event => event.status == 'to_reschedule')
    }, [events])

    const accessTypes = watchIsGroupTreatment
        ? t('agenda:group_treatment')
        : watchConsultants
          ? watchConsultants
                ?.filter(
                    (ec, index, self) =>
                        index === self.findIndex(e => e.accessType?.accessType === ec.accessType?.accessType)
                )
                ?.map(ec => (ec.accessType?.accessType ? t(eventTypeMapToLabel[ec.accessType.accessType]) : ''))
                .join(', ')
          : t('agenda:unknown_event_name')

    const fullNames = watchPatients
        ? watchPatients.length > 2
            ? `${watchPatients[0].fullName}, ${watchPatients[1].fullName} +${watchPatients.length - 2}`
            : watchPatients.map(ec => ec.fullName).join(', ')
        : ''

    const {mutate: updateEvent, isPending: isUpdatingEvent} = useEditEvent({
        onSuccess: () => {
            dialog?.hide()
            parentOnSuccess?.()
        },
        onError: error => {
            console.error(error)
            toast.error(error.message)
            parentOnError?.(error)
        }
    })

    const handleUpdateEvent = (formValues: EditEventFormConfirmationValidationSchema) => {
        const payload = eventFormValuesToUpdatePayload(formValues, {
            isRecurringEvent: !!isRecurringEvent,
            isDisabledBulkOperation,
            applyToFutureEvents,
            isChangedSubstitute: !!isChangedSubstitute,
            openCycleAccessType
        })

        updateEvent({
            urlParams: {eventId: formValues.id},
            payload
        })
    }

    const handleUpdateEventError: SubmitErrorHandler<EditEventFormConfirmationValidationSchema> = errors => {
        console.error(errors, {values: form.getValues()})
    }

    const onSubmit = form.handleSubmit(handleUpdateEvent, handleUpdateEventError)

    const handleApplyToFutureEventsChange = (value: boolean) => {
        setApplyToFutureEvents(value)
        if (
            value &&
            watchDate.date != previousDate &&
            defaultValues?.category == 'treatments' &&
            defaultValues?.repetitionData?.frequency &&
            !isChangedSubstitute
        ) {
            const frequency = defaultValues.repetitionData.frequency
            const dayOfMonth = dayjs(watchDate.date).date()
            const weekPositionInMonth = getWeekPositionInMonth(dayOfMonth)
            form.setValue('repetitionData', {
                frequency: frequency,
                position: frequency == 'weekly' ? 1 : weekPositionInMonth,
                dayOfWeek: dayjs(watchDate.date).format('dddd').toLowerCase() as Exclude<DayOfWeek, 'sunday'>
            })
        }
    }

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={confirmationDialog}
                onConfirm={onSubmit}
                isConfirmPending={isUpdatingEvent}
                contentSlot={
                    !!isRecurringEvent &&
                    !isDisabledBulkOperation &&
                    (isChangedSubstitute && isToReschedule ? (
                        <Flexbox direction="column" gap={2}>
                            <Text
                                palette="neutral"
                                shade="500"
                                size="textSm"
                                style={{display: 'flex', alignItems: 'center', gap: 4}}
                            >
                                {t('commons:events')}:{' '}
                                {isFetching ? (
                                    <Skeleton height={18} width={100} />
                                ) : isError ? (
                                    <Text render="span" palette="danger" shade="700">
                                        {t('errors:default')}
                                    </Text>
                                ) : (
                                    <Text render="span" weight="500" palette="neutral" shade="950">
                                        {t('agenda:events_to_reschedule', {
                                            count: applyToFutureEvents ? eventsToReschedule.length : 1,
                                            total: eventsToReschedule.length
                                        })}
                                    </Text>
                                )}
                            </Text>
                            <Switch
                                size="sm"
                                label={t('agenda:apply_substitute_to_future_events')}
                                checked={applyToFutureEvents}
                                onChange={handleApplyToFutureEventsChange}
                            />
                        </Flexbox>
                    ) : (
                        <Flexbox direction="column" gap={2}>
                            <Switch
                                size="sm"
                                label={t('agenda:apply_to_future_events')}
                                checked={applyToFutureEvents}
                                onChange={handleApplyToFutureEventsChange}
                            />
                            {applyToFutureEvents &&
                                watchRepetitionData?.dayOfWeek &&
                                watchRepetitionData?.frequency &&
                                watchRepetitionData?.position && (
                                    <EventRepetitionFrequencyText
                                        frequency={watchRepetitionData.frequency}
                                        position={watchRepetitionData.position}
                                        dayOfWeek={watchRepetitionData.dayOfWeek}
                                    />
                                )}
                        </Flexbox>
                    ))
                }
                headerSlot={
                    <Flexbox direction="column" gap={2}>
                        <DialogHeading>{t('agenda:edit_event')}</DialogHeading>
                        <Text palette="neutral" shade="600" size="textSm">
                            {`${fullNames} - ${accessTypes}`}
                        </Text>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
